import { EditorSettingProvider } from '@blocksuite/affine-shared/services';
import { resetNativeSelection } from '@blocksuite/affine-shared/utils';
import { DisposableGroup } from '@blocksuite/global/disposable';
import type { IVec } from '@blocksuite/global/gfx';
import type { PointerEventState } from '@blocksuite/std';
import {
  BaseTool,
  type GfxModel,
  InteractivityIdentifier,
  isGfxGroupCompatibleModel,
} from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';

import {
  decidirNoInicioDoArraste,
  decidirQuandoODedoAnda,
} from '../tuninho/decidir-pan.js';
import { calPanDelta } from './panning-utils.js';

export enum DefaultModeDragType {
  /** Moving selected contents */
  ContentMoving = 'content-moving',
  /** Native range dragging inside active note block */
  NativeEditing = 'native-editing',
  /** Default void state */
  None = 'none',
  /**
   * Arrastando o VAZIO para mover o quadro, como no modo hand.
   *
   * Existe como variante do enum, e não como troca para o `PanTool`, por duas razões:
   * trocar de ferramenta no `pointerdown` faria o `click` seguinte ir para a outra
   * ferramenta — e é o `click` do `DefaultTool` que LIMPA a seleção ao clicar no vazio;
   * e obrigaria a restaurar a ferramenta no `pointerup`, coordenação que este arquivo
   * não precisa ter. O critério mora em `../tuninho/decidir-pan.ts`.
   */
  Panning = 'panning',
  /** Expanding the dragging area, select the content covered inside */
  Selecting = 'selecting',
}

export class DefaultTool extends BaseTool {
  static override toolName: string = 'default';

  private _edgeScrollingTimer: number | null = null;

  private readonly _clearDisposable = () => {
    if (this._disposables) {
      this._disposables.dispose();
      this._disposables = null;
    }
  };

  private readonly _clearSelectingState = () => {
    this._stopEdgeScrolling();
    this._clearDisposable();
    // O gesto acabou: o dedo não deve nada. Sem isto, um arraste de dedo que terminasse dentro
    // da folga deixaria o pendente vivo e o PRÓXIMO arraste — de qualquer ponteiro — herdaria
    // o carimbo de tempo do anterior, decidindo com o relógio errado.
    this._dedoPendente = null;
  };

  /**
   * O arraste de UM DEDO que ainda não disse o que é (card 1864291811674032036).
   *
   * Vive entre o `dragStart` e o instante em que o dedo sai da folga de `FOLGA_DO_DEDO` px.
   * Guarda só o carimbo do `pointerdown`, que é o que falta para `decidirQuandoODedoAnda`
   * responder — e o `dragType` nesse meio-tempo é `Panning`, não `Selecting`, porque o
   * `Panning` é o único que consegue não fazer NADA enquanto espera: o `Selecting` desenharia
   * a caixa por cima do quadro durante a espera, que é exatamente o que não pode aparecer.
   */
  private _dedoPendente: { inicioMs: number } | null = null;

  private _disposables: DisposableGroup | null = null;

  private _scrollViewport(delta: IVec) {
    this.gfx.viewport.applyDeltaCenter(delta[0], delta[1]);
  }

  private _spaceTranslationRect: null | {
    w: number;
    h: number;
    startX: number;
    startY: number;
    endX: number;
    endY: number;
  } = null;

  private readonly _enableEdgeScrolling = (delta: IVec) => {
    this._stopEdgeScrolling();
    this._scrollViewport(delta);

    this._edgeScrollingTimer = window.setInterval(() => {
      this._scrollViewport(delta);
    }, 30);
  };

  private readonly _stopEdgeScrolling = () => {
    if (this._edgeScrollingTimer) {
      clearInterval(this._edgeScrollingTimer);
      this._edgeScrollingTimer = null;
    }
  };

  private _toBeMoved: GfxModel[] = [];

  private readonly _updateSelection = () => {
    const { gfx } = this;

    if (gfx.keyboard.spaceKey$.peek() && this._spaceTranslationRect) {
      const { w, h, startX, startY, endX, endY } = this._spaceTranslationRect;
      const { endX: lastX, endY: lastY } = this.controller.draggingArea$.peek();

      const dx = lastX - endX;
      const dy = lastY - endY;

      this.controller.draggingArea$.value = {
        x: Math.min(startX + dx, lastX),
        y: Math.min(startY + dy, lastY),
        w,
        h,
        startX: startX + dx,
        startY: startY + dy,
        endX: endX + dx,
        endY: endY + dy,
      };
    }

    const elements = this.interactivity?.handleBoxSelection({
      box: this.controller.draggingArea$.peek(),
    });

    if (!elements) return;

    this.selection.set({
      elements: elements.map(el => el.id),
      editing: false,
    });
  };

  dragType = DefaultModeDragType.None;

  movementDragging = false;

  /**
   * Get the end position of the dragging area in the model coordinate
   */
  get dragLastPos() {
    const { endX, endY } = this.controller.draggingArea$.peek();

    return [endX, endY] as IVec;
  }

  /**
   * Get the start position of the dragging area in the model coordinate
   */
  get dragStartPos() {
    const { startX, startY } = this.controller.draggingArea$.peek();

    return [startX, startY] as IVec;
  }

  get selection() {
    return this.gfx.selection;
  }

  get interactivity() {
    return this.std.getOptional(InteractivityIdentifier);
  }

  private async _cloneContent() {
    const clonedResult = await this.interactivity?.requestElementClone({
      elements: this._toBeMoved,
    });

    if (!clonedResult) return;

    this._toBeMoved = clonedResult.elements;
    this.selection.set({
      elements: this._toBeMoved.map(e => e.id),
      editing: false,
    });
  }

  private _determineDragType(evt: PointerEventState): DefaultModeDragType {
    const { x, y } = this.controller.lastMousePos$.peek();
    if (this.selection.isInSelectedRect(x, y)) {
      return this.selection.editing
        ? DefaultModeDragType.NativeEditing
        : DefaultModeDragType.ContentMoving;
    } else {
      const checked = this.interactivity?.handleElementSelection(evt);

      if (checked) {
        return DefaultModeDragType.ContentMoving;
      } else {
        // Aqui o upstream já concluiu que NÃO há nada sob o cursor — `handleElementSelection`
        // devolve falso antes de mexer em seleção nenhuma. A definição de "vazio" continua
        // sendo a dele; o que se decide agora é o que fazer com o vazio.
        const decisao = decidirNoInicioDoArraste({
          tipoDePonteiro: evt.raw.pointerType,
          comShift: evt.keys.shift,
          ajuste: this.std.getOptional(EditorSettingProvider)?.setting$.peek()
            .edgelessSelectDragPan,
        });

        if (decisao === 'selecionar') return DefaultModeDragType.Selecting;

        // `esperar-o-dedo` entra como `Panning` SEGURADO: o `dragMove` abaixo não aplica
        // movimento nenhum enquanto `_dedoPendente` estiver de pé. `evt.raw` aqui é o evento
        // de `pointerdown` — o `DragController` passa o estado do início ao `dragStart`
        // (`framework/std/src/event/control/pointer.ts:239`) —, então este carimbo é a hora em
        // que o dedo POUSOU, que é a única que serve para medir o tempo parado.
        this._dedoPendente =
          decisao === 'esperar-o-dedo'
            ? { inicioMs: evt.raw.timeStamp }
            : null;

        return DefaultModeDragType.Panning;
      }
    }
  }

  private initializeDragState(
    dragType: DefaultModeDragType,
    event: PointerEventState
  ) {
    this.dragType = dragType;

    this._clearDisposable();
    this._disposables = new DisposableGroup();

    // If the drag type is selecting, set up the dragging area disposable group
    // If the viewport updates when dragging, should update the dragging area and selection
    if (this.dragType === DefaultModeDragType.Selecting) {
      this._disposables.add(
        this.gfx.viewport.viewportUpdated.subscribe(() => {
          if (
            this.dragType === DefaultModeDragType.Selecting &&
            this.controller.dragging$.peek()
          ) {
            this._updateSelection();
          }
        })
      );
      return;
    }

    if (this.dragType === DefaultModeDragType.ContentMoving) {
      if (this.interactivity) {
        this.doc.captureSync();
        this.interactivity.handleElementMove({
          movingElements: this._toBeMoved,
          event: event.raw,
          onDragEnd: () => {
            this.doc.captureSync();
          },
        });
      }
      return;
    }
  }

  override click(e: PointerEventState) {
    if (this.doc.readonly) return;

    if (!this.interactivity?.handleElementSelection(e)) {
      this.selection.clear();
      resetNativeSelection(null);
    }

    this.interactivity?.dispatchEvent('click', e);
  }

  override deactivate() {
    this._stopEdgeScrolling();
    this._clearDisposable();
  }

  override doubleClick(e: PointerEventState) {
    if (this.doc.readonly) {
      const viewport = this.gfx.viewport;
      if (viewport.zoom === 1) {
        this.gfx.fitToScreen();
      } else {
        // Zoom to 100% and Center
        const [x, y] = viewport.toModelCoord(e.x, e.y);
        viewport.setViewport(1, [x, y], true);
      }
      return;
    }

    this.interactivity?.dispatchEvent('dblclick', e);
  }

  override dragEnd(e: PointerEventState) {
    this.interactivity?.dispatchEvent('dragend', e);

    if (this.selection.editing || !this.movementDragging) return;

    this.movementDragging = false;
    this._toBeMoved = [];
    this._clearSelectingState();
    this.dragType = DefaultModeDragType.None;
  }

  override dragMove(e: PointerEventState) {
    this.interactivity?.dispatchEvent('dragmove', e);

    if (!this.movementDragging) {
      return;
    }

    const { viewport } = this.gfx;
    switch (this.dragType) {
      case DefaultModeDragType.Selecting: {
        // Record the last drag pointer position for auto panning and view port updating
        this._updateSelection();
        const moveDelta = calPanDelta(viewport, e);
        if (moveDelta) {
          this._enableEdgeScrolling(moveDelta);
        } else {
          this._stopEdgeScrolling();
        }
        break;
      }
      case DefaultModeDragType.Panning: {
        // ── o dedo que ainda não disse o que é (card 1864291811674032036) ──────────────────
        //
        // `e.start` é onde o dedo POUSOU (o `DragController` monta cada move com
        // `startX: start.x`), e `e.raw.timeStamp` é a hora DESTE movimento — a mesma base do
        // carimbo guardado no `dragStart`, porque os dois saem de `PointerEvent.timeStamp`.
        if (this._dedoPendente) {
          const dx = e.point.x - e.start.x;
          const dy = e.point.y - e.start.y;
          const decisao = decidirQuandoODedoAnda({
            deslocamento: Math.hypot(dx, dy),
            msDesdeOToque: e.raw.timeStamp - this._dedoPendente.inicioMs,
          });

          // Ainda na folga: o que se mexeu foi o tremor, não o dedo. Nada acontece — e é este
          // `break` que impede o quadro de escorregar debaixo de quem está segurando parado.
          if (decisao === 'esperar-o-dedo') break;

          this._dedoPendente = null;

          if (decisao === 'selecionar') {
            // A caixa nasce de onde o dedo pousou, não daqui: quem desenha é o
            // `controller.draggingArea$`, ancorado no início do arraste pelo framework. E o
            // quadro NÃO andou durante a espera, então a troca não deixa rastro na tela.
            this.initializeDragState(DefaultModeDragType.Selecting, e);
            this._updateSelection();
            break;
          }
          // `mover-o-quadro`: cai para o pan abaixo, e o primeiro deslocamento aplicado é o
          // deste movimento — os ~10px da folga ficam para trás de propósito, que é o mesmo
          // "slop" que toda rolagem nativa tem antes de engatar.
        }

        // `e.delta` é INCREMENTAL — `offsetX - last.point.x`, e `last` é o move anterior
        // (`event/control/pointer.ts:221`), não o início do arraste. O sinal é invertido
        // porque mover o CENTRO para a esquerda é o que faz o conteúdo acompanhar o ponteiro
        // para a direita; é a mesma conta do `PanTool` (`lastX - e.x`).
        this._scrollViewport([-e.delta.x / viewport.zoom, -e.delta.y / viewport.zoom]);
        break;
      }
      case DefaultModeDragType.ContentMoving: {
        break;
      }
      case DefaultModeDragType.NativeEditing: {
        // TODO reset if drag out of note
        break;
      }
    }
  }

  // oxlint-disable-next-line typescript/no-misused-promises
  override async dragStart(e: PointerEventState) {
    const { preventDefaultState, handledByView } =
      this.interactivity?.dispatchEvent('dragstart', e) ?? {};

    if (this.selection.editing || preventDefaultState || handledByView) return;

    this.movementDragging = true;

    // Determine the drag type based on the current state and event
    let dragType = this._determineDragType(e);

    const elements = this.selection.selectedElements;
    if (elements.some(e => e.isLocked())) return;

    const toBeMoved = new Set(elements);

    elements.forEach(element => {
      if (isGfxGroupCompatibleModel(element)) {
        element.descendantElements.forEach(ele => {
          toBeMoved.add(ele);
        });
      }
    });

    this._toBeMoved = Array.from(toBeMoved);

    // If alt key is pressed and content is moving, clone the content
    if (dragType === DefaultModeDragType.ContentMoving && e.keys.alt) {
      await this._cloneContent();
    }

    // Set up drag state
    this.initializeDragState(dragType, e);
  }

  override mounted() {
    this.disposable.add(
      effect(() => {
        const pressed = this.gfx.keyboard.spaceKey$.value;

        if (pressed) {
          const currentDraggingArea = this.controller.draggingArea$.peek();

          this._spaceTranslationRect = currentDraggingArea;
        } else {
          this._spaceTranslationRect = null;
        }
      })
    );
  }

  override pointerDown(e: PointerEventState): void {
    this.interactivity?.dispatchEvent('pointerdown', e);
  }

  override pointerMove(e: PointerEventState) {
    this.interactivity?.dispatchEvent('pointermove', e);
  }

  override pointerUp(e: PointerEventState) {
    this.interactivity?.dispatchEvent('pointerup', e);
  }

  override unmounted(): void {}
}
